import { computed, ref } from 'vue'
import { api } from '@/global/services/api'
import { isArray, isFunction } from '@/global/utils/utils'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction } from '@/global/components/table/helpers/tableActions'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  buildRequestStreetsEndpoint,
  REQUESTS_TRANSLATION_NAMESPACE as NS,
  REQUEST_STREETS_TABLE_KEY
} from '@/modules/road-maintenance-module/winter-service-v2/requests/config/constants'

/**
 * The route tab (Popis ulica) of a request: the streets of the task it was made from,
 * listed beside the map they were drawn on.
 *
 * Look-only. The streets belong to the task, and the task's own screen is where they are
 * drawn and changed - so a street here can be opened and read, and nothing else.
 */
export function useRequestStreets (options) {
  const { translate, requestId, openStreetDialogFn } = options || {}

  const { buildConfig } = useTableService()

  const streets = ref([])

  /**
   * This endpoint answers with the streets under a `data` key of its own alongside the
   * totals, and it does not paginate - so the rows are fetched here and handed to the
   * table, which pages through them itself.
   */
  const fetchStreets = async () => {
    try {
      const { data } = await api(WINTER_SERVICE_MODULE).get(buildRequestStreetsEndpoint(requestId))

      if (isArray(data)) {
        streets.value = data
        return
      }

      streets.value = isArray(data?.data) ? data.data : []
    }
    catch (exception) {
      console.error(exception)
      streets.value = []
    }
  }

  /**
   * Opens a street. Reached two ways - from its row in the table and from the row's own
   * action menu - so both the table event and the street itself are accepted. It only
   * ever shows what the street says: the request does not own these streets.
   */
  const onOpenStreet = (event) => {
    const street = event?.data ?? event

    if (!street?.id || !isFunction(openStreetDialogFn)) {
      return
    }

    openStreetDialogFn({
      title: translate(`${NS}.street_details`),
      street
    })
  }

  // The listing reports the path type as its own object and leaves a polygon's length,
  // width and passes empty - the columns say so rather than showing a blank.
  const cellsConfig = computed(() => ({
    path_type: {
      custom_display_value: (data) => data?.path_type?.description || data?.path_type?.name || ''
    },
    passes: {
      custom_display_value: (data) => data?.passes ?? '-'
    },
    length: {
      custom_display_value: (data) => data?.length ?? '-'
    },
    width: {
      custom_display_value: (data) => data?.width ?? '-'
    },
    area: {
      custom_display_value: (data) => data?.area ?? '-'
    }
  }))

  const headers = computed(() => ([
    {
      label: translate(`${NS}.id`),
      key: 'id',
      always_visible: true
    },
    {
      label: translate(`${NS}.street_type`),
      key: 'path_type',
      always_visible: true
    },
    {
      label: translate(`${NS}.street_name`),
      key: 'street_name',
      always_visible: true
    },
    {
      label: translate(`${NS}.passes`),
      key: 'passes',
      always_visible: true
    },
    {
      label: translate(`${NS}.length_m`),
      key: 'length',
      always_visible: true
    },
    {
      label: translate(`${NS}.width_m`),
      key: 'width',
      always_visible: true
    },
    {
      label: translate(`${NS}.area_m2`),
      key: 'area',
      always_visible: true
    },
    {
      label: translate(`${NS}.city_district`),
      key: 'street_district',
      always_visible: true
    }
  ]))

  // A street can be looked at and nothing else - this screen never changes one.
  const actionsConfig = computed(() => ([
    showDetailsAction(translate, onOpenStreet)
  ]))

  const tableConfig = computed(() => buildConfig({
    key: REQUEST_STREETS_TABLE_KEY,
    // The table lives inside a tab, not on a screen of its own - it fills the tab and
    // scrolls its rows internally.
    fillHeight: true,
    // The rows are fetched here rather than by the table: the endpoint keeps them one
    // level down, beside the totals, and hands over the whole route at once.
    allowDataFetch: false,
    tableData: streets.value,
    internalPagination: {
      enable: true
    },
    // Clicking the row opens the street, the same way its action menu does.
    rowDblClick: onOpenStreet,
    // `search_and_buttons_config` is deliberately left out rather than emptied: the row
    // renders as soon as the key is there, and there is nothing to put in it.
    // Nothing in the row below it applies either - a look-only route needs no refresh,
    // no filters and no column picker.
    filters_and_columns_visibility_config: {
      showRefreshButton: false,
      showFiltersButton: false,
      showColumnsVisibilityButton: false,
      showTableGridSwitchButton: false
    },
    globalStyle: {
      // With no toolbar row above it the table starts at the container's own padding,
      // which left it sitting lower than the map next to it.
      containerStyle: {
        padding: 0
      }
    },
    tableConfig: {
      cellsConfig: cellsConfig.value,
      headers: headers.value,
      actionsConfig: actionsConfig.value
    }
  }))

  return {
    streets,
    tableConfig,
    fetchStreets
  }
}
